import { AnyID, Item, LinkedItem, Partition, SceneObjects } from '@types'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { Matrix4 } from 'three'
import isItem from './assertions/isItem'
import isLinkedItem from './assertions/isLinkedItem'
import isPartition from './assertions/isPartition'
import isWall from './assertions/isWall'

export type TransformableSelectionItem = Item | LinkedItem | Partition

export type SelectionTransformSnapshot = {
  pivotId: AnyID
  pivotStartMatrix: Matrix4
  itemStartMatrices: Map<AnyID, Matrix4>
}

export type ApplySelectionTransformOptions = {
  /** Matriz del pivote antes del cambio (sidebar / edición incremental sin snapshot previo). */
  previousPivotMatrix?: Matrix4
}

const TRANSFORMABLE_TYPES = new Set(['items', 'linkedItem', 'partition'])

export function isTransformableSelectionItem(
  item: SceneObjects | undefined,
): item is TransformableSelectionItem {
  return !!item && TRANSFORMABLE_TYPES.has(item.type)
}

/**
 * Captura las matrices mundiales al inicio de un drag grupal.
 * El pivote es el item que el usuario arrastra (currentObjectID).
 */
export function captureSelectionTransformSnapshot(
  selectedIds: Iterable<AnyID>,
  items: Map<string, SceneObjects>,
  pivotId: AnyID,
): SelectionTransformSnapshot | null {
  const pivotItem = items.get(pivotId)
  if (!isTransformableSelectionItem(pivotItem)) return null

  const itemStartMatrices = new Map<AnyID, Matrix4>()
  let hasSibling = false

  for (const id of selectedIds) {
    const item = items.get(id)
    if (!isTransformableSelectionItem(item)) continue
    itemStartMatrices.set(id, item.matrix.clone())
    if (id !== pivotId) hasSibling = true
  }

  if (!hasSibling) return null

  return {
    pivotId,
    pivotStartMatrix: pivotItem.matrix.clone(),
    itemStartMatrices,
  }
}

/** delta = M_pivot_new × M_pivot_start⁻¹ */
export function computeGroupDeltaMatrix(
  pivotStartMatrix: Matrix4,
  currentPivotMatrix: Matrix4,
): Matrix4 {
  return currentPivotMatrix.clone().multiply(pivotStartMatrix.clone().invert())
}

/** Aplica el delta de grupo a las matrices iniciales del snapshot. */
export function applyDeltaToSnapshot(
  snapshot: SelectionTransformSnapshot,
  currentPivotMatrix: Matrix4,
): Map<AnyID, Matrix4> {
  const delta = computeGroupDeltaMatrix(snapshot.pivotStartMatrix, currentPivotMatrix)
  const result = new Map<AnyID, Matrix4>()

  snapshot.itemStartMatrices.forEach((startMatrix, id) => {
    result.set(id, delta.clone().multiply(startMatrix))
  })

  return result
}

/** Delta incremental sin snapshot (p. ej. sidebar). */
export function applyIncrementalGroupTransform(
  selectedIds: Iterable<AnyID>,
  items: Map<string, SceneObjects>,
  pivotId: AnyID,
  previousPivotMatrix: Matrix4,
  newPivotMatrix: Matrix4,
): Map<AnyID, Matrix4> | null {
  const delta = computeGroupDeltaMatrix(previousPivotMatrix, newPivotMatrix)
  const result = new Map<AnyID, Matrix4>()
  let hasSibling = false

  for (const id of selectedIds) {
    const item = items.get(id)
    if (!isTransformableSelectionItem(item)) continue

    if (id === pivotId) {
      result.set(id, newPivotMatrix.clone())
    } else {
      hasSibling = true
      result.set(id, delta.clone().multiply(item.matrix))
    }
  }

  return hasSibling ? result : null
}

export function isItemTransformable(item: SceneObjects | undefined): boolean {
  return isItem(item) || isLinkedItem(item) || isPartition(item)
}

function getMaxWallHeight(
  items: Map<string, SceneObjects>,
  snappedWall?: string,
): number | null {
  if (snappedWall) {
    const wall = items.get(snappedWall)
    if (isWall(wall)) return wall.height
  }

  let maxHeight = 0
  items.forEach((sceneObject) => {
    if (isWall(sceneObject) && sceneObject.height > maxHeight) {
      maxHeight = sceneObject.height
    }
  })
  return maxHeight > 0 ? maxHeight : null
}

/** Restringe Y en vista 3D/alzados sin colisiones ni snap. */
export function restrictDragY(
  y: number,
  mode: string,
  items: Map<string, SceneObjects>,
  options?: { height?: number; snappedWall?: string },
): number {
  const is3DOrAlzados =
    mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS'

  if (!is3DOrAlzados) return y

  let restrictedY = Math.max(0, y)

  if (options?.height) {
    const maxWallHeight = getMaxWallHeight(items, options.snappedWall)
    if (maxWallHeight !== null) {
      restrictedY = Math.min(restrictedY, maxWallHeight - options.height)
    }
  }

  return restrictedY
}

/** Matriz del pivote durante drag: conserva rotación base + posición del gizmo local. */
export function buildPivotDragMatrix(pivotBaseMatrix: Matrix4, localDragMatrix: Matrix4): Matrix4 {
  const itemrot = toQuaternion()
  const itemPosition = toVector3()
  pivotBaseMatrix.decompose(toVector3(), itemrot, toVector3())
  localDragMatrix.decompose(itemPosition, toQuaternion(), toVector3())
  return pivotBaseMatrix.makeRotationFromQuaternion(itemrot).setPosition(itemPosition) as Matrix4
}

/**
 * Traslación grupal en multi-select: solo restricción Y, sin colisiones ni snap.
 * Evita que el pivote active collideItems contra hermanos seleccionados o cercanos.
 */
export function buildMultiSelectPivotDragMatrix(
  pivotBaseMatrix: Matrix4,
  localDragMatrix: Matrix4,
  mode: string,
  items: Map<string, SceneObjects>,
  yOptions?: { height?: number; snappedWall?: string },
): Matrix4 {
  const dragMatrix = buildPivotDragMatrix(pivotBaseMatrix, localDragMatrix)
  const pos = toVector3()
  dragMatrix.decompose(pos, toQuaternion(), toVector3())
  const restrictedY = restrictDragY(pos.y, mode, items, yOptions)
  return dragMatrix.clone().setPosition(pos.x, restrictedY, pos.z)
}
